import type { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { addSegment, setLivePoints, setLiveResult, setMeasureMode } from "../../../store/slices/measureSlice";
import type { Coordinate, DrawGeometry } from "../../draw/types";
import
  {
    EDIT_MEASURE_LAYER_ID,
    EDIT_MEASURE_SOURCE_ID,
    MEASURE_DRAWING_FEATURE_COLOR,
    MEASURE_DRAWING_FEATURE_STROKE_COLOR,
    MEASURE_DRAWING_FEATURE_STROKE_WIDTH,
    MEASURE_FEATURE_COLOR,
    MEASURE_FEATURE_KEY,
    MEASURE_FEATURE_STROKE_COLOR,
    MEASURE_FEATURE_STROKE_WIDTH,
    MEASURE_LAYER_ID,
    MEASURE_MODIFY_FEATURE_COLOR,
    MEASURE_MODIFY_FEATURE_STROKE_COLOR,
    MEASURE_MODIFY_FEATURE_STROKE_WIDTH,
    MEASURE_SAVED_LAYER_ID,
    MEASURE_SAVED_SOURCE_ID,
    MEASURE_SOURCE_ID,
  } from "../consts";
import type { MeasureMode, MeasureSegment } from "../types";
import { formatArea, formatDistance, lineDistance, polygonArea } from "./common";

type LngLatEvent = { lngLat: { lng: number; lat: number } };

type MLMap = {
  on: (ev: string, h: (e: unknown) => void) => void;
  off: (ev: string, h: (e: unknown) => void) => void;
  setLayoutProperty: (id: string, k: string, v: unknown) => void;
  setPaintProperty: (id: string, k: string, v: unknown) => void;
  addSource: (id: string, spec: object) => void;
  removeSource: (id: string) => void;
  getSource: (id: string) => undefined | { setData(d: object): void };
  addLayer: (spec: object, before?: string) => void;
  removeLayer: (id: string) => void;
  getLayer: (id: string) => unknown;
  getCenter: () => { lat: number; lng: number };
  getZoom: () => number;
  getBearing: () => number;
  getPitch: () => number;
  dragPan: { enable(): void; disable(): void };
  queryRenderedFeatures: (pt: unknown, opts: { layers: string[] }) => { properties: Record<string, unknown> }[];
  doubleClickZoom: { enable(): void; disable(): void };
  getCanvas: () => { style: CSSStyleDeclaration };
  isStyleLoaded: () => boolean;
  once: (type: string, listener?: (ev: unknown) => void) => Promise<unknown>;
};
interface MapMeasureState
{
  map: MLMap | null;
  mode: MeasureMode;
  dead: boolean;
  ready: boolean;
  addedIds: Set<string>;
  drawPts: [number, number][];
  mousePos: Coordinate | null;
  livePoints: Coordinate[];
  rectStart: [number, number] | null;
  rectEnd: [number, number] | null;
  moveEndH: ((e: unknown) => void) | null;
  clickH: ((e: unknown) => void) | null;
  dblH: ((e: unknown) => void) | null;
  mdH: ((e: unknown) => void) | null;
  mmH: ((e: unknown) => void) | null;
  muH: ((e: unknown) => void) | null;
  mClickH: ((e: unknown) => void) | null;
  mDblH: ((e: unknown) => void) | null;
  mMoveH: ((e: unknown) => void) | null;
  mEditClickH: ((e: unknown) => void) | null;
  mEditMoveH: ((e: unknown) => void) | null;
  mEditUpH: ((e: unknown) => void) | null;
  lastClickMs: number;
  editDrag: { segId: string; vIdx: number } | null;
  dragIdx: number;
  dragId: string | null;
  dragGeom: DrawGeometry | null;
  segments: MeasureSegment[];
}
// ── Helpers ───────────────────────────────────────────────────────────────────

/** Extract [lng, lat] from a map event */
const toLngLat = (e: unknown): Coordinate =>
{
  const ev = e as LngLatEvent;
  return [ev.lngLat.lng, ev.lngLat.lat];
};

export function createMapLibreMeasure(
  map: any,
  mode: MeasureMode,
  segments: MeasureSegment[],
  dispatch: Dispatch<UnknownAction>,
  allowMultiple = false,
)
{
  const S: MapMeasureState = {
    map,
    mode,
    dead: false,
    ready: false,
    addedIds: new Set(),
    drawPts: [],
    mousePos: null,
    livePoints: [],
    rectStart: null,
    rectEnd: null,
    moveEndH: null,
    clickH: null,
    dblH: null,
    mdH: null,
    mmH: null,
    muH: null,
    mClickH: null,
    mDblH: null,
    mMoveH: null,
    mEditClickH: null,
    mEditMoveH: null,
    mEditUpH: null,
    lastClickMs: 0,
    editDrag: null,
    dragIdx: -1,
    dragId: null,
    dragGeom: null,
    segments: segments ?? [],
  };

  return {
    activate()
    {
      if (!map) return;

      // Detach old handlers
      if (S.mClickH)
      {
        map.off("click", S.mClickH);
        S.mClickH = null;
      }
      if (S.mDblH)
      {
        map.off("dblclick", S.mDblH);
        S.mDblH = null;
      }
      if (S.mMoveH)
      {
        map.off("mousemove", S.mMoveH);
        S.mMoveH = null;
      }

      // Clear live overlay when tool is off or edit — draw handlers must not be active
      if (!mode || mode === "NONE" || mode === "EDIT")
      {
        map.getSource(MEASURE_SOURCE_ID)?.setData({ type: "FeatureCollection", features: [] });
        pushMeasureSaved(S);
        return;
      }

      initMeasureLayers(S);

      map.doubleClickZoom.disable();
      // map.getCanvas().style.cursor = "crosshair";

      if (mode != "VIEW") map.getCanvas().style.cursor = "crosshair";

      const mMoveH = (e: unknown) =>
      {
        S.mousePos = toLngLat(e) as [number, number];
        pushMeasureLive(S);
      };
      const mClickH = (e: unknown) =>
      {
        // Guard: ignore if this click is part of a double-click (< 350ms apart)
        const now = Date.now();
        if (now - S.lastClickMs < 350) return;
        S.lastClickMs = now;
        S.drawPts.push(toLngLat(e) as [number, number]);
        S.livePoints = S.drawPts;
        dispatch(setLivePoints((S.livePoints || []).slice()));
        if (mode === "DISTANCE" && S.livePoints.length >= 2) dispatch(setLiveResult(formatDistance(lineDistance(S.drawPts))));
        else if (mode === "AREA" && S.livePoints.length >= 3) dispatch(setLiveResult(formatArea(polygonArea(S.drawPts))));
        else dispatch(setLiveResult(""));

        pushMeasureLive(S);
      };
      const mDblH = () =>
      {
        S.lastClickMs = Date.now(); // prevent following click

        const pts = [...S.drawPts];
        S.drawPts = [];
        S.livePoints = [];
        S.mousePos = null;
        dispatch(setLiveResult(""));
        if (pts.length < 2) return;
        let result = "";
        if (mode === "DISTANCE") result = formatDistance(lineDistance(pts));
        else if (mode === "AREA" && pts.length >= 3) result = formatArea(polygonArea(pts));
        const seg: MeasureSegment = { id: newSegmentId(), points: pts, result, type: mode };
        dispatch(addSegment(seg));
        dispatch(setLivePoints([]));
        S.segments.push(seg);
        // Clear live; saved will be pushed by segments state update
        map?.getSource(MEASURE_SOURCE_ID)?.setData({ type: "FeatureCollection", features: [] });

        pushMeasureSaved(S);
        if (allowMultiple)
        {
          resetTool(S);
        } else
        {
          removeTool(S);
          dispatch(setMeasureMode("NONE"));
        }
      };
      map.on("mousemove", mMoveH);
      map.on("click", mClickH);
      map.on("dblclick", mDblH);
      S.mMoveH = mMoveH;
      S.mClickH = mClickH;
      S.mDblH = mDblH;
    },

    deactivate()
    {
      map.doubleClickZoom.enable();
      map.getCanvas().style.cursor = "default";
      S.dead = true;
      S.ready = false;

      removeTool(S);

      try
      {
        if (S.mClickH) map.off("click", S.mClickH);
        if (S.mDblH) map.off("dblclick", S.mDblH);
        if (S.mMoveH) map.off("mousemove", S.mMoveH);
        detachToolHandlers(map, S);
      } catch
      {
        /* ignore — already removed or container gone */
      }

      S.map = null;
      S.moveEndH = null;
      clearState(S);

      try
      {
        map.dragPan.enable();
      } catch
      {
        /* */
      }
    },
  };
}

const initMeasureLayers = (S: MapMeasureState) =>
{
  // Measure: saved (permanent) + live (rubber-band)
  const { map, segments: segs } = S;
  if (!map) return;
  // avoid calls before style is ready
  if (typeof map.isStyleLoaded === "function" && !map.isStyleLoaded())
  {
    const onceKey = "__measureLayersPending";
    if (!(map as any)[onceKey])
    {
      (map as any)[onceKey] = true;
      map.once("styledata", () =>
      {
        (map as any)[onceKey] = false;
        initMeasureLayers(S);
      });
    }
    return;
  }

  try
  {
    const addLayerIfMissing = (spec: object) =>
    {
      const id = (spec as { id: string }).id;
      if (!map.getLayer(id)) map.addLayer(spec);
    };

    const emptyFC = { type: "FeatureCollection", features: [] } as const;

    const sourceSaved = map.getSource(MEASURE_SAVED_SOURCE_ID);
    if (!sourceSaved) map.addSource(MEASURE_SAVED_SOURCE_ID, { type: "geojson", data: emptyFC });
    else sourceSaved.setData({ type: "FeatureCollection", features: segmentsToFeatures(segs) });

    addLayerIfMissing({
      id: `${MEASURE_SAVED_LAYER_ID}-line`,
      type: "line",
      source: MEASURE_SAVED_SOURCE_ID,
      paint: { "line-color": MEASURE_FEATURE_STROKE_COLOR, "line-width": MEASURE_FEATURE_STROKE_WIDTH },
      filter: ["==", ["geometry-type"], "LineString"],
    });

    addLayerIfMissing({
      id: `${MEASURE_SAVED_LAYER_ID}_draw-line`,
      type: "line",
      source: MEASURE_SAVED_SOURCE_ID,
      paint: { "line-color": ["coalesce", ["get", "color"], MEASURE_FEATURE_STROKE_COLOR], "line-width": MEASURE_FEATURE_STROKE_WIDTH },
      filter: ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]],
    });

    addLayerIfMissing({
      id: `${MEASURE_SAVED_LAYER_ID}-fill`,
      type: "fill",
      source: MEASURE_SAVED_SOURCE_ID,
      paint: { "fill-color": MEASURE_FEATURE_COLOR, "fill-opacity": 0.18 },
      filter: ["==", ["geometry-type"], "Polygon"],
    });
    addLayerIfMissing({
      id: `${MEASURE_SAVED_LAYER_ID}-pt`,
      type: "circle",
      source: MEASURE_SAVED_SOURCE_ID,
      paint: {
        "circle-radius": 4,
        "circle-color": MEASURE_FEATURE_COLOR,
        "circle-stroke-width": MEASURE_FEATURE_STROKE_WIDTH,
        "circle-stroke-color": MEASURE_FEATURE_STROKE_COLOR,
      },
    });

    const sourceDrawing = map.getSource(MEASURE_SOURCE_ID);
    if (!sourceDrawing) map.addSource(MEASURE_SOURCE_ID, { type: "geojson", data: emptyFC });
    addLayerIfMissing({
      id: `${MEASURE_LAYER_ID}-line`,
      type: "line",
      source: MEASURE_SOURCE_ID,
      paint: { "line-color": MEASURE_DRAWING_FEATURE_STROKE_COLOR, "line-width": MEASURE_DRAWING_FEATURE_STROKE_WIDTH, "line-dasharray": [4, 3] },
      filter: ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]],
    });
    addLayerIfMissing({
      id: `${MEASURE_LAYER_ID}-fill`,
      type: "fill",
      source: MEASURE_SOURCE_ID,
      paint: { "fill-color": MEASURE_DRAWING_FEATURE_COLOR, "fill-opacity": 0.18 },
      filter: ["==", ["geometry-type"], "Polygon"],
    });
    addLayerIfMissing({
      id: `${MEASURE_LAYER_ID}-point`,
      type: "circle",
      source: MEASURE_SOURCE_ID,
      paint: {
        "circle-radius": 5,
        "circle-color": MEASURE_DRAWING_FEATURE_COLOR,
        "circle-stroke-width": MEASURE_DRAWING_FEATURE_STROKE_WIDTH,
        "circle-stroke-color": MEASURE_DRAWING_FEATURE_STROKE_COLOR,
      },
    });

    // Edit vertex handles source
    const sourceEdit = map.getSource(EDIT_MEASURE_SOURCE_ID);
    if (!sourceEdit) map.addSource(EDIT_MEASURE_SOURCE_ID, { type: "geojson", data: emptyFC });

    addLayerIfMissing({
      id: `${EDIT_MEASURE_LAYER_ID}-selected`,
      type: "line",
      source: EDIT_MEASURE_SOURCE_ID,
      paint: { "line-color": MEASURE_MODIFY_FEATURE_STROKE_COLOR, "line-width": MEASURE_MODIFY_FEATURE_STROKE_WIDTH },
      filter: ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]],
    });
    addLayerIfMissing({
      id: `${EDIT_MEASURE_LAYER_ID}-fill`,
      type: "fill",
      source: EDIT_MEASURE_SOURCE_ID,
      paint: { "fill-color": MEASURE_MODIFY_FEATURE_COLOR, "fill-opacity": 0.18 },
      filter: ["==", ["geometry-type"], "Polygon"],
    });
    addLayerIfMissing({
      id: `${EDIT_MEASURE_LAYER_ID}-vertex`,
      type: "circle",
      source: EDIT_MEASURE_SOURCE_ID,
      paint: {
        "circle-radius": 7,
        "circle-color": MEASURE_MODIFY_FEATURE_COLOR,
        "circle-stroke-width": MEASURE_MODIFY_FEATURE_STROKE_WIDTH,
        "circle-stroke-color": MEASURE_MODIFY_FEATURE_STROKE_COLOR,
        "circle-pitch-alignment": "map",
      },
      filter: ["==", ["geometry-type"], "Point"],
    });
  } catch { }
};

// ── Measure helpers ──────────────────────────────────────────────────────────
/** Push live (in-progress) drawing + rubber-band to SRC_MEASURE */
const pushMeasureLive = (S: MapMeasureState) =>
{
  const { map } = S;
  if (!map) return;
  const pts = S.livePoints;
  const cur = S.mousePos;
  const features: object[] = [];

  // Build preview coords (pts + cursor)
  const preview = cur && pts.length > 0 ? [...pts, cur] : pts;
  if (preview.length >= 2)
  {
    // const isArea = S?.mode === "AREA";
    const isArea = S.mode === "AREA" && preview.length > 2;
    features.push({
      type: "Feature",
      geometry: {
        type: isArea ? "Polygon" : "LineString",
        coordinates: isArea ? [[...preview, preview[0]]] : preview,
      },
      properties: { preview: true },
    });

    if (cur)
    {
      features.push({ type: "Feature", geometry: { type: "Point", coordinates: cur } });
    }
  }
  // Confirmed vertex dots
  pts.forEach((p: Coordinate) => features.push({ type: "Feature", geometry: { type: "Point", coordinates: p }, properties: {} }));
  map.getSource(MEASURE_SOURCE_ID)?.setData({ type: "FeatureCollection", features });
};

/** Push all finished segments to SRC_MEASURE_SAVED */
const pushMeasureSaved = (S: MapMeasureState) =>
{
  const { map } = S;
  if (!map) return;
  map.getSource(MEASURE_SAVED_SOURCE_ID)?.setData({ type: "FeatureCollection", features: segmentsToFeatures(S.segments) });
};

const newSegmentId = () => `${MEASURE_FEATURE_KEY}-${crypto.randomUUID()}`;

const segmentsToFeatures = (segs: MeasureSegment[]): object[] =>
{
  const features: object[] = [];
  for (const seg of segs)
  {
    const pts = seg.points;
    if (pts.length < 2) continue;
    const useArea = seg.type === "AREA" && pts.length >= 3;
    features.push({
      type: "Feature",
      geometry: {
        type: useArea ? "Polygon" : "LineString",
        coordinates: useArea ? [[...pts, pts[0]]] : pts,
      },
      properties: { segId: seg.id },
    });
    for (const p of pts)
    {
      features.push({ type: "Feature", geometry: { type: "Point", coordinates: p }, properties: { segId: seg.id } });
    }
  }
  return features;
};
const clearState = (S: MapMeasureState) =>
{
  S.drawPts = [];
  S.dragIdx = -1;
  S.dragId = null;
  S.dragGeom = null;
  S.mousePos = null;
  S.livePoints = [];
  S.rectStart = null;
  S.rectEnd = null;
};

function resetTool(S: MapMeasureState)
{
  clearState(S);
  S.segments = [];
}

function removeTool(S: MapMeasureState)
{
  if (S.map) detachToolHandlers(S.map, S);
  resetTool(S);
}

function detachToolHandlers(m: MLMap, S: MapMeasureState)
{
  if (S.clickH) m.off("click", S.clickH);
  if (S.dblH) m.off("dblclick", S.dblH);
  if (S.mdH) m.off("mousedown", S.mdH);
  if (S.mmH) m.off("mousemove", S.mmH);
  if (S.muH) m.off("mouseup", S.muH);
  S.clickH = S.dblH = null;
  S.mdH = S.mmH = S.muH = null;
  try
  {
    m.dragPan.enable();
  } catch
  {
    /* */
  }
}
